import { Component, OnInit, Input, Output, EventEmitter, isDevMode, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { ComponentBase } from 't4core';
import { AppIntegrationService, AppSettingsService } from 't4-app-integration';
import { PhotoModel } from '../../Views/take-pictures/take-pictures.component';
import { eSeverityLevel } from '@microsoft/applicationinsights-web';

@Component({
    selector: 'app-photo-view',
    templateUrl: './photo-view.component.html',
    styleUrls: ['./photo-view.component.css'],
    standalone: false
})
export class PhotoViewComponent extends ComponentBase implements OnInit {

  @Input() public bookingId: number;
  @Input() public event: string = "Pickup";

  @Input() public photo: PhotoModel;
  @Input() public additionalPhoto: boolean = false;

  @Output() public completed = new EventEmitter<PhotoModel>();
  @Output() public deleted = new EventEmitter<PhotoModel>();
  @Output() public failed = new EventEmitter<PhotoModel>();

  @ViewChild('video') private videoRef: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') private canvasRef: ElementRef<HTMLCanvasElement>;

  public working: boolean = false;
  public cameraOpen: boolean = false;
  public cameraLoading: boolean = false;
  public fallbackImage: string = "/Images/fallback-image-photo.png";

  private mediaStream: MediaStream = null;
  public showCameraOverlay = false;

  public videoReady: boolean =  false;

  constructor(private appService: AppIntegrationService, private appSettings: AppSettingsService, private cd: ChangeDetectorRef) { super(); }

  ngOnInit(): void {
  }

  private logDiagnostic(action: string, properties: { [key: string]: string } = {}, failed: boolean = false, error?: any) {
    try {
      this.Insights.trackDetailedTrace('PhotoView: ' + action,
        failed ? eSeverityLevel.Error : eSeverityLevel.Information, '', {
          rentalId: String(this.bookingId ?? ''),
          photoEvent: this.event,
          angleId: String(this.photo?.angle?.Id ?? 0),
          additionalPhoto: String(this.additionalPhoto),
          cameraPermission: String(this.appService.Device?.Camera),
          cameraOpen: String(this.cameraOpen),
          videoReady: String(this.videoReady),
          online: String(navigator.onLine),
          ...properties,
          errorName: typeof error?.name === 'string' ? error.name : '',
          httpStatus: typeof error?.status === 'number' ? String(error.status) : ''
        });
    } catch {
      // Diagnostics must not interrupt the photo workflow.
    }
  }

  public async startBrowserCamera(): Promise<boolean> {
    this.logDiagnostic('Camera start requested');
    this.cameraLoading = true;
    this.cameraOpen = false;
    this.showCameraOverlay = true;
    this.videoReady = false;

    this.cd.detectChanges();
    await new Promise(res => setTimeout(res, 0));

    var started = false;

    if (this.appSettings.meetsMinimumAppVersion(4.9)) {
      started = await this.startBrowserCameraInternal();
    } else {
      this.logDiagnostic('Browser camera skipped: minimum app version not met');
    }

    if (!started) {
      this.logDiagnostic('Native camera fallback started');
      this.showCameraOverlay = false;
      try {
        await this.takePhoto();

        if (this.photo && this.photo.taken) {
          this.logDiagnostic('Native camera fallback completed');
          this.cameraLoading = false;
          this.cameraOpen = false;
          return true;
        }
      } catch (ex) {
        this.logDiagnostic('Native camera fallback failed', {}, true, ex);
      }

      this.cameraLoading = false;
      this.cameraOpen = false;
      this.showCameraOverlay = false;
      this.logDiagnostic('Camera start failed', {}, true);
      this.failed.emit(this.photo);
      return false;
    }

    this.cameraLoading = false;
    this.cameraOpen = true;
    return true;
  }

  private async startBrowserCameraInternal(): Promise<boolean> {
    let stage = 'getUserMedia';
    this.logDiagnostic('Browser camera start requested', { secureContext: String(window.isSecureContext) });

    if (!navigator.mediaDevices?.getUserMedia) {
      this.logDiagnostic('Browser camera API unavailable', { secureContext: String(window.isSecureContext) }, true);
      return false;
    }

    try {
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false
        });
      } catch (error) {
        this.logDiagnostic('Rear camera request failed; retrying any camera', {}, true, error);
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
      }

      const videoEl = this.videoRef?.nativeElement as HTMLVideoElement | undefined;
      if (!videoEl) {
        this.logDiagnostic('Video element unavailable after opening overlay', {}, true);
        return false;
      }

      videoEl.autoplay = true;
      videoEl.muted = true;
      videoEl.playsInline = true;
      videoEl.setAttribute('autoplay', '');
      videoEl.setAttribute('muted', '');
      videoEl.setAttribute('playsinline', '');
      videoEl.setAttribute('webkit-playsinline', '');

      videoEl.srcObject = this.mediaStream;
      stage = 'video readiness';

      await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          videoEl.removeEventListener('loadedmetadata', finish);
          videoEl.removeEventListener('loadeddata', finish);
          videoEl.removeEventListener('canplay', finish);
          resolve();
        };

        videoEl.addEventListener('loadedmetadata', finish, { once: true });
        videoEl.addEventListener('loadeddata', finish, { once: true });
        videoEl.addEventListener('canplay', finish, { once: true });

        setTimeout(finish, 1000);
      });
      stage = 'video playback';
      await videoEl.play();
      this.videoReady = true;

      const track = this.mediaStream.getVideoTracks()?.[0];
      this.logDiagnostic('Browser camera ready', {
        trackState: String(track?.readyState),
        trackEnabled: String(track?.enabled),
        trackMuted: String(track?.muted),
        videoWidth: String(videoEl.videoWidth),
        videoHeight: String(videoEl.videoHeight)
      });
      this.cameraOpen = true;
      this.cameraLoading = false;
      return true;
    } catch (ex: any) {
      this.logDiagnostic('Browser camera start failed', { stage, secureContext: String(window.isSecureContext) }, true, ex);
      return false;
    }
  }

  private stopBrowserCamera() {
    try {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(t => t.stop());
      }
    } catch (ex){
      this.logDiagnostic('Camera track cleanup failed', {}, true, ex);
    }
    this.mediaStream = null;
    if (this.videoRef && this.videoRef.nativeElement) {
      try { this.videoRef.nativeElement.pause(); this.videoRef.nativeElement.srcObject = null; }
      catch (error) { this.logDiagnostic('Video cleanup failed', {}, true, error); }
    }
    this.cameraOpen = false;
    this.cameraLoading = false;
    this.showCameraOverlay = false;
    this.videoReady = false;

    // ensure view updates
    try { this.cd.detectChanges(); }
    catch (error) {
      this.logDiagnostic('Camera view refresh failed', {}, true, error);
    }


  }

  private captureFromVideo(): string {
    const video = this.videoRef?.nativeElement;
    const canvas = this.canvasRef?.nativeElement;
    if (!video || !canvas) return null;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    // Return data URL
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  // Called from template when user presses "Capture" in browser camera UI
  public async captureAndUpload() {
    this.logDiagnostic('Browser capture started');
    try {
      this.working = true;
      const dataUrl = this.captureFromVideo();
      this.stopBrowserCamera();

      if (!dataUrl) {
        this.logDiagnostic('Browser capture returned no image', {}, true);
        this.working = false;
        this.failed.emit(this.photo);
        return;
      }

      // Strip data URL header to get raw base64 payload
      const base64 = dataUrl.split(',')[1];

      this.photo.data = base64;
      this.photo.taken = true;

      // Upload to server in same way as native
      var angle = 0;
      if (this.photo.angle) angle = this.photo.angle.Id;

      try {
        this.logDiagnostic('Browser upload started');
        const storedUri = await this.Api.post<string>("Data/Store" + this.event + "Photo", base64, { angleId: angle, rentalId: this.bookingId });
        if (storedUri) {
          this.photo.photoUri = storedUri;
          this.logDiagnostic('Browser upload succeeded');
        } else {
          this.logDiagnostic('Browser upload returned no URI; local preview retained', {}, true);
          // fall back to embedding the captured image for preview
          this.photo.photoUri = dataUrl;
        }
      } catch (uploadEx) {
        this.logDiagnostic('Browser upload failed; local preview retained', {}, true, uploadEx);
        // fallback: keep the captured image locally as dataUrl so user sees it
        this.photo.photoUri = dataUrl;
      }
      this.logDiagnostic('Browser capture completed');
      
      this.completed.emit(this.photo);
    } catch (ex) {
      this.logDiagnostic('Browser capture failed', {}, true, ex);
      this.failed.emit(this.photo);
    } finally {
      this.working = false;
      this.stopBrowserCamera();
    }
  }

  // Called from template when user cancels browser camera UI
  public cancelBrowserCamera() {
    this.logDiagnostic('Browser camera cancelled');
    this.stopBrowserCamera();
  }



  public async takePhoto() {
    // Check if demo settings is enabled for photos
    var demoMode = false;
    if (localStorage.getItem('takePhoto')) {
      var demoMode = localStorage.getItem('takePhoto') == "demo" ? true : false;
    }

    if (isDevMode() || demoMode) {
      this.logDiagnostic('Demo photo requested');
      if (!this.photo.photoUri) {
        this.working = true;
        this.photo.taken = true;
        this.photo.photoUri = "https://encrypted-tbn0.gstatic.com/images?q=tbn%3AANd9GcQpNI7ZwMcvI7BTW67q8IEVEr04ngMxps1QujZt89RjTNm46RfB&usqp=CAU";
        this.completed.emit(this.photo);
      }
      this.working = false;
    }
    else {

      if (!this.photo.photoUri) {
        this.working = true;
        var angle = 0;
        if (this.photo.angle) angle = this.photo.angle.Id;
        var uploadUri = this.Settings.getApiBase() + "Data/Store" + this.event + "Photo?angleId=" + angle + "&rentalId=" + this.bookingId;

        this.logDiagnostic('Native camera requested');
        let p;
        try {
          p = await this.appService.takePhoto(uploadUri, this.Settings.getToken().access_token);
        } catch (error) {
          this.logDiagnostic('Native camera request failed', {}, true, error);
          throw error;
        }

        if (p && p.Success) {
          this.photo.taken = true;
          try {
            this.logDiagnostic('Native camera succeeded', { hasImage: String(!!p.Image) });

            if (p.Image && p.Image.length > 0) {
              this.logDiagnostic('Native image upload started');
              this.Api.post<string>("Data/Store" + this.event + "Photo", p.Image, { angleId: angle, rentalId: this.bookingId }).then(
                uri => this.logDiagnostic('Native image upload finished', { hasStoredUri: String(!!uri) }, !uri),
                error => this.logDiagnostic('Native image upload failed', {}, true, error));
              this.photo.data = p.Image;
            }

            this.completed.emit(this.photo);
          }
          catch (ex) {
            this.logDiagnostic('Native photo completion failed', {}, true, ex);
            this.completed.emit(this.photo);
          }

        } else {
          this.logDiagnostic('Native camera returned no successful photo', { hasResult: String(!!p) }, true);

          this.failed.emit(this.photo);
        }

        this.working = false;
      }
    }
  }

  public async deletePhoto(event) {
    event.stopPropagation();
    this.logDiagnostic('Photo deletion requested');

    this.photo.photoUri = null;
    this.photo.data = null;
    this.photo.taken = false;

    this.deleted.emit(this.photo);
  }
}
